import { useEffect, useState, useContext, useRef } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';

import { useQuery, useQueries } from '@tanstack/react-query';
import { useApi } from '../api/useApi';
import { useThermalZone } from '../Comfort/useThermalZone';
import { useCampusMeta, useCampusControlModes } from '../../context/CampusContextProvider';

import WithRouter from '../../utilities/WithRouter';
import {
  setCurrentCampus,
  setCurrentBuilding,
  setCurrentZone,
  setCurrentZoneData,
  setCurrentGX,
  updateCurrentCampusData,
} from '../../actions';

import { setThermalZoneStatus, getHvacMode } from '../../utilities/ProcessRealtimeZoneHelper';
import { THERMAL_ZONE_STATUS } from '../../config/status';
import { CONTROL_MODE } from '../../config/control';
import { ACTIVITY } from '../../config/activity';
import { ICON } from '../../config/icons';

import { AppContext } from '../../context/AppContext';
import { DataContext } from '../../context/DataContextProvider';
import { useIsReadOnly } from '../../context/UserContextProvider';

import CurrentWeather from '../../components/CurrentWeather';

import ComfortIcon from '../../components/icons/ComfortIcon';
import { TempSettingsLockup } from '../../components/TempSettingsLockup/TempSettingsLockup';
import { SelectOption } from '../../components/SelectOption/SelectOption';
import { ThermalZoneStatusPill } from '../../components/ThermalZoneStatus/ThermalZoneStatusPill';
import { ActivityDisplay } from '../../components/ActivityDisplay/ActivityDisplay';
import { DataCard } from '../../components/DataCard/DataCard';
import { Button } from '../../components/Button/Button';
import { Header } from '../../components/Header/Header';
import { Breadcrumb } from '../../components/Breadcrumb/Breadcrumb';
import { useModal } from '../modal/useModal';
import { ToggleWithIcon } from '../../components/ToggleWithIcon/ToggleWithIcon';

import { PerformanceChart } from '../Charts/PerformanceChart';
import { performanceChartConfiguration } from '../Charts/configs/performance-chart';
import { PermissionBlock } from '../permissions/PermissionBlock';

import './ComfortDetail.scss';
import Loading from '../../components/Loading';

const ComfortDetail = ({
  router,
  currentCampusData,
  showMobile,
  errorMessage,
  setErrorMessage,
}) => {
  const isReadOnly = useIsReadOnly();
  const { fetchDataJson, fetchJson } = useApi();
  const campusMeta = useCampusMeta();
  const thermalZoneControlModes = useCampusControlModes();
  const { openModal } = useModal();
  const errorMessageRef = useRef(null);

  const {
    updateThermalZoneSchedule,
    filterActiveZoneEvents,
    handleLock,
    handleManageOptimized,
    handleCancelOverride,
    getActiveEventControlModes,
  } = useThermalZone();
  const [currentThermalZoneStatus, setCurrentThermalZoneStatus] = useState(null);
  const [scheduleOptions, setScheduleOptions] = useState([]);
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [activeEvents, setActiveEvents] = useState([]);
  const [controlModes, setControlModes] = useState([]);
  const [controlModeOverrides, setControlModeOverrides] = useState([]);
  const [controlModeEvents, setControlModeEvents] = useState([]);
  const [hasRemoteOverride, setHasRemoteOverride] = useState(false);

  const [isKeypadLocked, setIsKeypadLocked] = useState(null);
  const [isManaged, setIsManaged] = useState(null);
  const [isOffline, setIsOffline] = useState(null);
  const [showLoading, setShowLoading] = useState({ message: '', isLoading: false });

  // get router params
  const campusId = router.params.campusId;
  const buildingId = router.params.buildingId;
  const zoneId = router.params.zoneId;
  const campusName = campusMeta?.campusName || 'Campus';

  // get zone data
  const {
    data: zoneData,
    isLoading,
    error,
    refetch: refetchZoneData,
  } = useQuery({
    queryKey: ['zoneData', zoneId],
    queryFn: () => fetchDataJson(`/ahiapi/${zoneId}/thermalZone`),
    refetchInterval: 5 * 1000, // 5 seconds
  });

  // get zone event data
  const {
    data: zoneEventData,
    isLoading: zoneEventDataIsLoading,
    error: zoneEventDataError,
    refetch: refetchZoneEventData,
  } = useQuery({
    queryKey: ['zoneEventData', zoneId],
    queryFn: () => fetchDataJson(`/ahiapi/${zoneId}/thermalZone/events`),
    refetchInterval: 15 * 1000, // 15 seconds
  });

  // get campus event data
  // const {
  //   data: campusEventData,
  //   isLoading: campusEventDataIsLoading,
  //   error: campusEventDataError,
  // } = useQuery({
  //   queryKey: ['campusEventData', campusId],
  //   queryFn: () => fetchDataJson(`/ahiapi/${campusId}/events`),
  //   // refetchInterval: 5 * 1000, // 5 seconds
  // });

  // get campus schedule data
  const {
    data: scheduleData,
    isLoading: scheduleDataIsLoading,
    error: scheduleDataError,
  } = useQuery({
    queryKey: ['scheduleData', campusId],
    queryFn: () => fetchDataJson(`/ahiapi/${campusId}/schedules/${`small`}`),
    refetchInterval: 15 * 1000, // 15 seconds
  });

  const {
    longThermalZoneDisplayName: zoneName,
    temperature,
    temperatureLowerBound,
    temperatureUpperBound,
    statusCategory,
    activityState: activity,
  } = zoneData || {};

  const zoneStatus = setThermalZoneStatus(zoneData);
  // const isUnmanaged = zoneStatus?.key === THERMAL_ZONE_STATUS.UNMANAGED.key;
  const isInOverride = zoneData?.inOverride || zoneData?.inLocalOverride || false;

  const hvacMode = isInOverride
    ? ACTIVITY.IN_OVERRIDE
    : getHvacMode(zoneData)?.key !== 'auto'
      ? getHvacMode(zoneData)
      : 'unavailable';

  useEffect(() => {
    setCurrentThermalZoneStatus(setThermalZoneStatus(zoneData));
    setActiveSchedule(zoneData?.scheduleId);
    setIsKeypadLocked(zoneData?.localOverrideLocked || false);
    setIsManaged(!zoneData?.unmanaged || false);
    setIsOffline(zoneData?.offline || false);
    checkOverrideStatus(zoneData);
  }, [zoneData]);

  useEffect(() => {
    if (!zoneEventData) {
      return;
    }
    setActiveEvents(filterActiveZoneEvents(zoneEventData));
  }, [zoneEventData]);

  useEffect(() => {
    setControlModeEvents(getActiveEventControlModes(activeEvents));
  }, [activeEvents]);

  useEffect(() => {
    if (isLoading) {
      setShowLoading({ message: 'Loading zone data...', isLoading: true });
    } else {
      setShowLoading({ message: '', isLoading: false });
    }
  }, [isLoading]);

  useEffect(() => {
    const combinedControlModes = [
      ...(controlModeOverrides || []),
      ...(controlModeEvents || []),
      ...(thermalZoneControlModes || []),
    ];
    setControlModes([...(combinedControlModes || [])]);
  }, [controlModeOverrides, thermalZoneControlModes, controlModeEvents]);

  useEffect(() => {
    setScheduleOptions(
      scheduleData?.map((schedule) => ({
        label: schedule.scheduleName,
        value: schedule.scheduleId,
      })) || [{ label: 'No schedules available', value: '' }]
    );
  }, [scheduleData]);

  if (!zoneData || isLoading) {
    return <></>;
  }

  function checkOverrideStatus(zoneData) {
    setControlModeOverrides([]);
    setHasRemoteOverride(false);

    if (zoneData?.inLocalOverride && !zoneData?.inOverride) {
      const localOverrideControlMode = {
        id: `localOverride-${zoneData.localOverrideEndTime}`,
        mode: 'LOCAL_OVERRIDE',
        isActive: true,
        duration: {
          startTime: null,
          endTime: zoneData.localOverrideEnds,
        },
        untilDesc: `${zoneData?.localOverrideEnds ? `Until ${new Date(zoneData.localOverrideEnds).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Indefinitely'}`,
        // TODO: decide on more readable date format
        // untilDesc: `${zoneData?.localOverrideEnds ? `Until ${zoneData.localOverrideEnds}` : 'Indefinitely'}`,
        // TODO: Handle when data is available
        // details: `Mode: ${getHvacMode(zoneData)?.label || 'Unavailable'}`,
      };
      setControlModeOverrides([localOverrideControlMode]);
    } else if (zoneData?.inOverride) {
      setHasRemoteOverride(true);
      const remoteOverrideControlMode = {
        id: `override-${zoneData?.overrideEnds ? zoneData.overrideEnds : 'indefinite'}`,
        mode: 'REMOTE_OVERRIDE',
        isActive: true,
        duration: {
          startTime: zoneData?.overrideStarts ? zoneData.overrideStarts : null,
          endTime: zoneData?.overrideEnds ? zoneData.overrideEnds : null,
        },
        // TODO: decide on more readable date format?
        // untilDesc: `${zoneData?.overrideEnds ? `Until ${zoneData.overrideEnds}` : 'Indefinitely'}`,
        untilDesc: `${zoneData?.overrideEnds ? `Until ${new Date(zoneData.overrideEnds).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Indefinitely'}`,
        // TODO: Handle when data is available
        // details: `Mode: ${getHvacMode(zoneData)?.label || 'Unavailable'}`,
        action: () => handleClickCancelOverride(),
        callBackLabel: 'cancel',
      };
      setControlModeOverrides([remoteOverrideControlMode]);
    }
  }

  const renderControlModes = () => {
    if (activeEvents.length === 0 && controlModes.length === 0) {
      return <DataCard label="Loading..." icon={ICON.ACTIVITY.DEFAULT} />;
    }
    if (isOffline) {
      return (
        <DataCard label="Offline" icon={ACTIVITY.OFFLINE.icon}>
          <div className="until-desc">Currently offline</div>
        </DataCard>
      );
    }
    if (!isManaged) {
      return (
        <DataCard label="Zone is Unmanaged" icon={ACTIVITY.UNMANAGED.icon}>
          <div className="until-desc">Zone management is disabled</div>
        </DataCard>
      );
    }
    if (!controlModes || controlModes.length === 0) {
      return <DataCard label="No Active Control Modes" icon={ACTIVITY.DEFAULT.icon} />;
    }

    const activeControlModes = controlModes.filter((mode) => {
      const targetMode = CONTROL_MODE[mode.mode.toUpperCase()];
      return targetMode && mode.isActive !== false;
    });

    if (activeControlModes.length === 0 && zoneData?.unmanaged === false) {
      return (
        <DataCard label="Standard" icon={CONTROL_MODE.STANDARD.icon}>
          <div className="">
            <div className="details">{`Elexity Managed`}</div>
          </div>
        </DataCard>
      );
    }

    return controlModes.map((controlMode, index) => {
      const targetMode = CONTROL_MODE[controlMode.mode.toUpperCase()];
      if (!targetMode) {
        // console.warn(`Control mode ${controlMode.mode} not found in CONTROL_MODE config`);
        return null;
      }
      if (controlMode.isActive === false) {
        return null;
      }

      const renderDescDetails = () => {
        if (controlMode.mode.toUpperCase() === 'STANDARD') {
          return (
            <div className="">
              <div className="details">{`Elexity Managed`}</div>
            </div>
          );
        }
        return (
          <div>
            {controlMode.untilDesc && <div className="until-desc">{controlMode.untilDesc}</div>}
            {controlMode.details && <div className="details">{controlMode.details}</div>}
          </div>
        );
      };

      return (
        <DataCard
          key={`mode.label-${index}`}
          label={targetMode.label}
          icon={targetMode.icon}
          callBackAction={controlMode.action}
          callBackLabel={controlMode?.callBackLabel || null}
          customLabel={controlMode?.customLabel || null}
        >
          {renderDescDetails()}
        </DataCard>
      );
    });
  };

  const renderHeaderLockup = () => {
    return (
      <>
        <ComfortIcon
          backgroundFill="none"
          fillColor={'#02222B'}
          strokeColor={'#02222B'}
          strokeWidth={2.5}
          width="18px"
        />
        <h1>Comfort</h1>
      </>
    );
  };

  const handleScheduleChange = (scheduleId) => {
    const scheduleName = scheduleData?.find(
      (schedule) => schedule.scheduleId === scheduleId
    )?.scheduleName;
    setShowLoading({ message: 'Updating schedule...', isLoading: true });
    updateThermalZoneSchedule({
      thermalZoneEntityKey: zoneId,
      scheduleName: scheduleName,
      successCallback: () => {
        setShowLoading({ message: '', isLoading: false });
      },
    });
  };

  const handleKeypadLockToggle = (checked) => {
    setIsKeypadLocked(checked);
    setShowLoading({ message: 'Updating lock status...', isLoading: true });
    handleLock({
      lockState: checked,
      thermalZoneEntityKey: zoneId,
      successCallback: () => {
        setTimeout(() => {
          setShowLoading({ message: '', isLoading: false });
          refetchZoneData();
        }, 1000);
      },
      failCallback: () => {
        setTimeout(() => {
          setShowLoading({ message: '', isLoading: false });
          refetchZoneData();
        }, 1000);
      },
    });
  };

  const handleOptimizedToggle = (checked) => {
    openModal('confirmation', {
      modalMessage: `Are you sure you want to set this zone to ${checked === true ? 'managed' : 'unmanaged'}?`,
      modalAction: () => {
        setIsManaged(checked);
        setShowLoading({ message: 'Updating...', isLoading: true });
        handleManageOptimized({
          managedState: checked,
          thermalZoneEntityKey: zoneId,
          successCallback: () => {
            setShowLoading({ message: '', isLoading: false });
          },
        });
      },
    });
  };

  const handleClickCancelOverride = () => {
    openModal('confirmation', {
      modalMessage: `Are you sure you want to cancel the remote override?`,
      modalAction: () => {
        setShowLoading({ message: 'Canceling override...', isLoading: true });
        handleCancelOverride({
          thermalZoneEntityKey: zoneId,
          successCallback: () => {
            setShowLoading({ message: '', isLoading: false });
            refetchZoneData();
            checkOverrideStatus(zoneData);
          },
        });
      },
    });
  };

  const currentScheduleName = scheduleData?.find(
    (schedule) => schedule.scheduleId === activeSchedule
  )?.scheduleName;

  return (
    <ComfortSection className={showMobile ? 'comfort-section mobile' : 'comfort-section desktop'}>
      <Header siteName={campusName} showWeather={currentCampusData}>
        <Breadcrumb
          iconHeader={renderHeaderLockup()}
          label={`All Zones`}
          targetPath={`/comfort/${campusId}`}
        />
      </Header>

      <div className="comfort-content comfort-content--detail">
        <div className="content-block ">
          <div className="zone-detail--header">
            <div>
              <div className="header-text--sm">Zone</div>
              <div className="zone-detail--name">
                <h2 className="header-text--main-content">{zoneName}</h2>
                <ThermalZoneStatusPill status={zoneStatus} />
              </div>
            </div>
          </div>

          <section className="zone-detail--temp-activity">
            <div className="temp">
              <div className="header-text--sm">Current Temperature</div>
              <TempSettingsLockup
                status={zoneStatus}
                temperature={temperature}
                tempBounds={{ min: temperatureLowerBound, max: temperatureUpperBound }}
                hvacMode={hvacMode}
                // noBgColor={true}
              />
            </div>
            <div className="activity">
              <div className="header-text--sm">Current Activity</div>
              <ActivityDisplay activity={activity.toUpperCase()} showLabel={true} />
            </div>
            <div className="schedule">
              <div className="header-text--sm">
                <span>Assigned Schedule</span>
                {/* TODO: Add the edit schedule functionality */}
                {/* <span className="link" onClick={() => openScheduleModal()}>
                  Edit Schedule
                </span> */}
              </div>
              <div>
                <PermissionBlock
                  readOnlyElement={
                    <div className="activity-label" style={{ marginTop: '18px' }}>
                      {currentScheduleName || 'No schedule assigned'}
                    </div>
                  }
                >
                  <SelectOption
                    options={scheduleOptions}
                    selectedValue={activeSchedule}
                    name="zoneSchedule"
                    initialLabel="Select a schedule"
                    onChangeCallback={handleScheduleChange}
                  />
                </PermissionBlock>
              </div>
            </div>
          </section>
          <section className="zone-detail--controls">
            <div className="mode">
              <div className="header-text--sm">Active Control Mode</div>
              {renderControlModes()}
            </div>
            <div className="controls">
              <div className="header-text--sm">Zone Controls</div>
              {/* <div className="controls-container"> */}
              <div className="create-controls">
                <div className="create-controls--buttons">
                  {!hasRemoteOverride ? (
                    <Button
                      label="Create Remote Override"
                      variant="add"
                      onClickCallback={() =>
                        openModal('remote-override', {
                          zoneData,
                          setShowLoading,
                          errorMessage,
                          setErrorMessage,
                          errorMessageRef,
                          updateZoneData: refetchZoneData,
                        })
                      }
                    />
                  ) : (
                    <Button
                      label="Cancel Remote Override"
                      variant="cancel"
                      onClickCallback={() => handleClickCancelOverride()}
                    />
                  )}
                  <Button
                    label="Create New Event"
                    variant="add"
                    onClickCallback={() =>
                      openModal('schedule-event', {
                        zoneData,
                        setShowLoading,
                        errorMessage,
                        setErrorMessage,
                        errorMessageRef,
                        updateZoneData: refetchZoneEventData,
                      })
                    }
                  />
                </div>
              </div>

              {/* </div> */}
            </div>
            <div className="manage-control">
              <div className="header-text--sm"></div>
              <ToggleWithIcon
                label="Keypad Locked"
                icon={isKeypadLocked ? ICON.LOCK.LOCKED : ICON.LOCK.UNLOCKED}
                onToggle={(checked) => handleKeypadLockToggle(checked)}
                isToggled={isKeypadLocked}
                isReadOnly={isReadOnly}
              />
              <ToggleWithIcon
                label="Elexity Managed"
                icon={ICON.CONTROL_MODE.ELEXITY}
                onToggle={(checked) => handleOptimizedToggle(checked)}
                isToggled={isManaged}
              />
            </div>
          </section>
        </div>
      </div>
      <div>
        <PerformanceChart
          entityKey={zoneId}
          subtitle={`${zoneName}`}
          graphConfiguration={performanceChartConfiguration}
        />
      </div>
      {(showLoading.isLoading && <Loading message={showLoading.message} />) || null}
    </ComfortSection>
  );
};

const ComfortSection = styled.section`
  color: #02222b;
  overflow: auto;

  .building-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0px 2px 6px #02222b14;
    display: flex;
    flex-flow: column;
    margin-bottom: 24px;
    padding: 24px 24px 12px;
    row-gap: 12px;

    .building-header {
      align-items: center;
      display: flex;
      padding: 0 0 12px 12px;

      h3 {
        font-size: 14px;
        font-weight: 600;
        margin: 0 0 0 12px;
      }
    }

    .building-empty-state {
      align-items: center;
      display: flex;
      gap: 12px;
      justify-content: center;
      padding: 26px 24px 24px;
    }

    //ZONE BUTTON ONLY ON MAIN COMFORT PAGE
    .thermal-zones .location {
      cursor: pointer;
      text-decoration: underline;
      transition: ease-in-out all 250ms;

      &:hover {
        color: rgba(2, 34, 43, 0.5);
        transition: ease-in-out all 250ms;
      }
    }

    table {
      border-collapse: collapse;
      font-size: 14px;
      text-align: left;
      width: 100%;

      th:nth-of-type(1),
      td:nth-of-type(1) {
        max-width: 220px;
        width: 20%;
      }

      th:nth-of-type(2),
      td:nth-of-type(2),
      th:nth-of-type(4),
      td:nth-of-type(4) {
        max-width: 130px;
        width: 12%;
      }
      th:nth-of-type(3),
      td:nth-of-type(3) {
        max-width: 140px;
        width: 12%;
      }

      th:nth-of-type(5),
      td:nth-of-type(5),
      th:nth-of-type(6),
      td:nth-of-type(6) {
        max-width: 60px;
        width: 5%;
      }

      th:nth-of-type(7),
      td:nth-of-type(7) {
        max-width: 130px;
        width: 14%;
      }

      th {
        color: #02222b;
        font-weight: 500;
        padding: 0.625em 0.25em;
      }

      td {
        padding: 0.25em;
      }

      tr {
        align-items: center;
        border-bottom: 1px solid #edf1f2;
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
      }

      tr:last-of-type {
        border-bottom: none;
      }
    }
  }

  @media screen and (max-width: 900px) {
    // .header-container .header {
    //   padding-bottom: 0;
    // }

    .thermal-zones tr.labels {
      display: none;
    }

    .building-container .thermal-zones .location {
      max-width: 175px;
      width: 35%;
    }

    .building-container table td.temp-range {
      width: 100px;
      max-width: unset !important;
    }
  }

  @media screen and (max-width: 1024px) {
    th:nth-of-type(2),
    td:nth-of-type(2) {
      display: none;
    }
  }
`;

const mapStateToProps = (state) => {
  return {
    campuses: state.campuses,
    currentCampusId: state.currentCampusId,
    currentBuildingId: state.currentBuildingId,
    currentZoneId: state.currentZoneId,
    currentGXId: state.currentGXId,
    currentCampusData: state.currentCampusData,
    setCurrentZoneData: state.currentZoneData,
    currentUserData: state.currentUserData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setCurrentCampus: (campusId) => {
      dispatch(setCurrentCampus(campusId));
    },
    setCurrentBuilding: (buildingId) => {
      dispatch(setCurrentBuilding(buildingId));
    },
    setCurrentZone: (zoneId) => {
      dispatch(setCurrentZone(zoneId));
    },
    setCurrentZoneData: (zoneId) => {
      dispatch(setCurrentZoneData(zoneId));
    },
    setCurrentGX: (gxId) => {
      dispatch(setCurrentGX(gxId));
    },
    updateCurrentCampusData: (campusData) => {
      dispatch(updateCurrentCampusData(campusData));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WithRouter(ComfortDetail));
